import { specIdForFeed } from '@features/feedView/specForFeed';
import { formatCount } from '@features/feedView/format';
import { createTimeInstance } from '@utils/time';

// The "Score Feed Data" attachment's pure half: how a /feed/view response becomes one `feed_inputs`
// entry, what the entry may weigh, and what the chips say. No React, no network, so every rule
// here is unit-testable against a captured response.
//
// Deep imports from the feedView feature rather than its barrel, on purpose: the barrel re-exports
// panels whose import graph reaches the service layer and the flow node tree, and a pure module's
// test should not have to mock any of that to run.
//
// The limits are fapi's (ai_copilot app/models.py: FEED_INPUT_MAX_ENTRIES, FEED_INPUT_MAX_ROWS,
// FEED_INPUT_MAX_COMPANY_IDS, FEED_INPUT_FLOOR_COLUMNS) and Rails' (the 10 MB budget, which Rails
// checks against Content-Length with 1 MB of slack). Refusing here is a courtesy that saves a
// round trip; the servers are the real gate.
export const FEED_INPUTS_MAX_ENTRIES = 5;
export const FEED_INPUT_MAX_ROWS = 25000;
export const FEED_INPUT_MAX_COMPANY_IDS = 1000;
export const FEED_INPUTS_MAX_BYTES = 10 * 1024 * 1024;

// The columns fapi requires every entry to carry, in the order the entry lists them. /feed/view
// emits them on every row whether or not `columns` names them.
export const FEED_INPUT_FLOOR_COLUMNS = ['utc_knowable_at', 'ticker', 'company_id', 'score'];

// The one real capture of /feed/view (feedView/mockFeed.js, 2026-08-20) spells two floor columns
// differently from the documented names the current server emits. The normalizer keeps both; the
// wire entry has to send the documented ones, so they are renamed before anything else is read.
const ROW_KEY_ALIASES = { knowable_at: 'utc_knowable_at', symbol: 'ticker' };

// The entry allowlist. fapi's entry model is `extra: forbid`, so a client-only field reaching the
// wire is a 400 on the whole submit; building the entry from this list — rather than spreading the
// response — is what makes that structurally impossible.
export const WIRE_KEYS = [
  'feed_id',
  'feed_name',
  'knowable_from',
  'knowable_to',
  'company_ids',
  'market_cap_version',
  'resolved_dag_sha',
  'fetched_at',
  'columns',
  'rows',
  'row_count',
];

export const FEED_INPUT_SOURCES = { SPEC: 'spec', FEED: 'feed' };

// Which feeds a score spec publishes, out of the loaded GET /feeds list: `score_spec_<id>` for a
// single-score spec, one `score_spec_<id>__<score>` per score otherwise. The specs catalog carries no
// score names, so the list is the only place to learn them. specIdForFeed fails closed on a
// free-form feed_name, which is right here too: such a feed deliberately no longer says which spec
// produced it.
export const feedIdsForSpec = (specId, feeds) => {
  if (!specId) return [];
  return (feeds || []).filter((feedId) => specIdForFeed(feedId) === specId);
};

const isScalar = (value) =>
  value === null ||
  value === undefined ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

const asOptionalString = (value) => (typeof value === 'string' && value ? value : undefined);

const canonicalRow = (row) => {
  const source = row && typeof row === 'object' && !Array.isArray(row) ? row : {};
  const out = {};
  Object.keys(source).forEach((key) => {
    const canonical = ROW_KEY_ALIASES[key];
    // The documented spelling wins when a row carries both.
    if (canonical && canonical in source) return;
    out[canonical || key] = source[key];
  });
  return out;
};

// Column order: the floor first, then the server's own universe order for anything else it named,
// then whatever remains in first-seen order. Deterministic per response, and floor-first so the
// agent's first four columns are always the same four.
const orderColumns = ({ present, universe }) => {
  const seen = new Set();
  const ordered = [];
  const take = (name) => {
    if (!present.has(name) || seen.has(name)) return;
    seen.add(name);
    ordered.push(name);
  };
  FEED_INPUT_FLOOR_COLUMNS.forEach(take);
  universe.forEach(take);
  present.forEach(take);
  return ordered;
};

// One /feed/view response plus the body that asked for it -> one wire entry.
//
// Reads the RAW response, never normalizeViewResponse's output: that reshapes rows for the Feed
// View table (derived previous-score fields, member columns, a `payload` sub-object) and the agent
// should see the server's columns, not the UI's.
//
// Returns `{ entry, droppedColumns }` or `{ error }`. A dropped column is one with a non-scalar
// cell — the `inputs` markers object and the `members_missing` list a non-slim multi-input read
// carries — which the entry model refuses; the caller says so rather than silently losing them.
export const toFeedInput = (data, body, { now = () => new Date() } = {}) => {
  const rawRows = Array.isArray(data?.rows) ? data.rows : [];
  const rows = rawRows.map(canonicalRow);
  const universe = Array.isArray(data?.available_columns) ? data.available_columns.map(String) : [];

  const present = new Set();
  rows.forEach((row) => Object.keys(row).forEach((key) => present.add(key)));

  // Zero rows is a legitimate answer ("nothing knowable in this window") and still needs a column
  // list the server will accept: the floor, plus whatever was asked for that the feed does offer.
  if (!rows.length) {
    FEED_INPUT_FLOOR_COLUMNS.forEach((name) => present.add(name));
    (Array.isArray(body?.columns) ? body.columns : [])
      .filter((name) => universe.includes(name))
      .forEach((name) => present.add(name));
  }

  const droppedColumns = [];
  const columns = orderColumns({ present, universe }).filter((name) => {
    const flat = rows.every((row) => isScalar(row[name]));
    if (!flat) droppedColumns.push(name);
    return flat;
  });

  const missingFloor = FEED_INPUT_FLOOR_COLUMNS.find((name) => !columns.includes(name));
  if (missingFloor) {
    return { error: COPY.floorMissing(missingFloor) };
  }

  const entry = {
    feed_id: String(body?.feed_id || data?.feed_id || ''),
  };
  const feedName = asOptionalString(data?.feed_name);
  if (feedName) entry.feed_name = feedName;
  const knowableFrom = asOptionalString(body?.knowable_from);
  if (knowableFrom) entry.knowable_from = knowableFrom;
  const knowableTo = asOptionalString(body?.knowable_to);
  if (knowableTo) entry.knowable_to = knowableTo;
  if (Array.isArray(body?.company_ids) && body.company_ids.length) {
    entry.company_ids = body.company_ids.filter((id) => Number.isInteger(id));
  }
  const marketCapVersion = asOptionalString(data?.market_cap_version);
  if (marketCapVersion) entry.market_cap_version = marketCapVersion;
  const dagSha = asOptionalString(data?.resolved_dag_sha);
  if (dagSha) entry.resolved_dag_sha = dagSha;
  entry.fetched_at = now().toISOString();
  entry.columns = columns;
  entry.rows = rows.map((row) => columns.map((name) => (row[name] === undefined ? null : row[name])));
  entry.row_count = entry.rows.length;

  return { entry, droppedColumns };
};

// UTF-8 size of a string. TextEncoder where the environment has it; Blob where it does not (jsdom
// defines Blob but not always TextEncoder); a last-resort escape count where neither exists.
export const utf8Bytes = (value) => {
  const text = String(value);
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text).length;
  if (typeof Blob !== 'undefined') return new Blob([text]).size;
  return unescape(encodeURIComponent(text)).length;
};

export const entryBytes = (entry) => utf8Bytes(JSON.stringify(entry));

export const totalBytes = (items) => (items || []).reduce((sum, item) => sum + (item?.bytes || 0), 0);

export const overBudget = (items, nextBytes) => totalBytes(items) + nextBytes > FEED_INPUTS_MAX_BYTES;

export const hasFeed = (items, feedId) => (items || []).some((item) => item.id === feedId);

// Items whose rows are still in hand. After a kickoff each item keeps its count for the summary
// card and loses its rows (stripRows); such an item can neither be sent nor shown as attachable.
export const liveFeedInputs = (items) => (items || []).filter((item) => item && !item.rowsReleased);

// Whether a 400's invalid ids name a chip that is actually on the form. An id the server spells
// differently from the chip, or one left behind by a chip since removed, must not block Submit —
// nothing on the form could clear it.
export const hasInvalidFeedChip = (items, invalidIds) => {
  const invalid = new Set(invalidIds || []);
  return liveFeedInputs(items).some((item) => invalid.has(item.id));
};

// After a successful kickoff the rows have been sent and are only weight; the chip and the summary
// card need the count. `rowsReleased` marks the item as not resendable.
export const stripRows = (item) => ({ ...item, entry: { ...item.entry, rows: [] }, rowsReleased: true });

export const describeSize = (bytes) => {
  const n = typeof bytes === 'number' && isFinite(bytes) ? bytes : 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const describeRows = (count) => `${formatCount(count)} ${count === 1 ? 'row' : 'rows'}`;

const formatLocalDay = (value) => (value ? createTimeInstance(value).format('MMM D, YYYY') : null);

// The window the analyst picked, read back in LOCAL time. buildViewBody widened the picked days to
// their local edges before serializing them (toIsoStart / toIsoEnd), so the local date of each
// bound is the picked day exactly. The Feed View page formats its stored runs in UTC, which is
// right for a bound that was serialized as UTC midnight — and prints this end bound a day late
// anywhere west of Greenwich, since 23:59:59 local is already tomorrow in UTC.
export const describeWindow = (from, to) => {
  const fromLabel = formatLocalDay(from);
  const toLabel = formatLocalDay(to);
  if (fromLabel && toLabel) return `${fromLabel} → ${toLabel}`;
  if (fromLabel) return `from ${fromLabel}`;
  if (toLabel) return `to ${toLabel}`;
  return 'All history';
};

// Same rule the Feed View page's query summary applies to its company filter.
const describeCompanies = (companies) => {
  const list = companies || [];
  if (list.length === 1) return list[0].symbol || list[0].name || '1 company';
  if (list.length > 1) return `${formatCount(list.length)} companies`;
  return null;
};

// `<window> · <companies> · N rows · size` — what one chip says under the feed id.
export const chipSubtitle = (item) => {
  const entry = item?.entry || {};
  return [
    describeWindow(entry.knowable_from, entry.knowable_to),
    describeCompanies(item?.companies),
    describeRows(entry.row_count || 0),
    describeSize(item?.bytes),
  ]
    .filter(Boolean)
    .join(' · ');
};

// Everything this attachment says out loud, so a reword is one diff and the tests import what the
// app renders.
export const COPY = {
  fieldLabel: 'Score Feed Data',
  selectButton: 'Select Score Feed',
  removeAll: 'Remove All',
  maxEntriesHint: `Up to ${FEED_INPUTS_MAX_ENTRIES} feeds per request.`,
  removeChip: (feedId) => `Remove ${feedId}`,
  invalidFallback: 'Invalid score feed.',

  modalTitle: 'Select Score Feed',
  sourceLabel: 'Source',
  sourceSpec: 'Score spec',
  sourceFeed: 'Feed',
  feedKindLabel: 'Feed kind',
  specLabel: 'Score spec',
  specHint: 'Every feed the spec publishes is listed; remove the ones you do not want before attaching.',
  noFeedForSpec: 'No viewable feed for this spec yet.',
  loadingFeeds: 'Loading the feed list…',
  pendingFeedsLabel: 'Feeds to attach',
  feedLabel: 'Feed',
  windowLabel: 'Knowable window',
  windowHint: 'Rows whose scores became knowable in this window. Max is the whole history; Custom opens the dates.',
  companiesLabel: 'Companies',
  companiesHint: 'Optional. Paste tickers or company ids. Leave empty for every company in the feed.',
  payloadCheckbox: 'Include LLM payload columns',
  payloadHint:
    'Adds each row’s written reasoning (the spec’s extra columns). Off keeps the attachment small; a 25,000-row feed with reasoning can exceed the budget.',
  attach: 'Attach',
  attaching: (index, total) => `Attaching… ${index}/${total}`,
  fetching: 'Fetching rows',
  attached: (count) => `Attached ${count} feed${count === 1 ? '' : 's'}.`,
  done: 'Done',
  needFeed: 'Choose a feed or a score spec first.',
  zeroRows: (feedId) => `${feedId}: 0 rows for this window — attached anyway.`,
  droppedColumns: (feedId, names) => `${feedId}: left out ${names.join(', ')} — not flat columns.`,
  columnsRefused: (feedId) =>
    `${feedId}: the server refused the column request, so the rows carry the floor only (no sector, market cap or company name).`,
  alreadyAttached: (feedId) => `${feedId} is already attached — remove it first.`,
  tooManyEntries: `Up to ${FEED_INPUTS_MAX_ENTRIES} feeds per request — remove one first.`,
  tooManyCompanies: `Up to ${FEED_INPUT_MAX_COMPANY_IDS} companies per feed.`,
  tooManyRows: (count) => `${formatCount(count)} rows is over the ${formatCount(FEED_INPUT_MAX_ROWS)}-row cap.`,
  overBudget: (bytes, attached) =>
    `Too large (${describeSize(bytes)}; ${describeSize(attached)} of the ${describeSize(
      FEED_INPUTS_MAX_BYTES
    )} budget already attached) — narrow the window, filter companies, or turn off payload columns.`,
  floorMissing: (column) => `The feed response is missing ${column}; it cannot be attached.`,
  tryAgain: 'Try again',

  summaryLabel: 'Score Feed Data',
  summaryNone: 'None',
};
